import { useReactiveVar } from '@apollo/client'
import { Box, Chip, Collapse, Hidden, IconButton, Typography, styled } from '@mui/material'
import MuiToolbar from '@mui/material/Toolbar'
import { useTheme } from '@mui/styles'
import { Menu } from '@mui/icons-material'
import { BucketShare, BucketShareType } from '../../../graphql/generated'
import { activeScrollingVar, drawerVar } from '../../../providers/apollo/cache'
import useBucketShareAlbums from '../hooks/useBucketShareAlbums'

const StyledToolbar = styled('div')(() => ({
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1
}))

export default function Toolbar({ bucketShare }: Readonly<{ bucketShare: BucketShare }>) {
  const theme = useTheme()
  const scrolling = useReactiveVar(activeScrollingVar)
  const { shouldRenderShareDrawer } = useBucketShareAlbums(bucketShare)
  const mediaCount = bucketShare.totalMedia
  const itemSuffix = mediaCount === 1 ? 'Item' : 'Items'
  const getTitle = () => {
    if (bucketShare.title) {
      return bucketShare.title
    }

    if (bucketShare.shareType === BucketShareType.Media) {
      return 'Shared Media'
    }

    return bucketShare.shareType === BucketShareType.Bucket ? 'Shared Bucket' : 'Shared Album'
  }

  const title = getTitle()

  return (
    <StyledToolbar>
      <Collapse appear={false} in={scrolling !== 'down'}>
        <MuiToolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: '5px 10px', sm: '0 10px' },
            backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#fff'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center'
            }}
          >
            {shouldRenderShareDrawer && (
              <Hidden mdUp>
                <IconButton
                  color="primary"
                  onClick={() => drawerVar('shareAlbumsDrawer')}
                  id="appbar-drawer-toggle"
                  disableRipple
                  disableTouchRipple
                  aria-label="Open Menu"
                  data-test="appbar-drawer-toggle"
                >
                  <Menu />
                </IconButton>
              </Hidden>
            )}
            <Typography variant="h6" sx={{ lineHeight: '1.2', fontWeight: 500 }}>
              <span data-test="album-main-title">{title}</span>
            </Typography>
            {bucketShare?.shareType !== BucketShareType.Bucket && (
              <Chip variant="outlined" label={`${mediaCount} ${itemSuffix}`} size="small" data-test="chips-item-count" />
            )}
          </Box>
        </MuiToolbar>
      </Collapse>
    </StyledToolbar>
  )
}
